import CachedIcon from "@mui/icons-material/Cached";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Formik } from "formik";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import prettyBytes from "pretty-bytes";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { deleteFile } from "../../../api/file/deleteFile";
import { downloadFile } from "../../../api/file/downloadFile";
import { getStageFiles } from "../../../api/file/getStageFiles";
import { getStageRole } from "../../../api/stage";
import { saveComplaint } from "../../../api/stage/complaints";
import apm from "../../../apm";
import { useTranslationHook } from "../../../hooks";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { setLoading } from "../../../store/UI";
import { ProjectFile } from "../../../types/model/file";
import { Procedure, ProcedureRole } from "../../../types/model/procedure";
import {
  Complaint,
  ComplaintRequest,
  ComplaintRequestSchema,
  Stage,
  StageRole,
} from "../../../types/model/stage";
import { formatDate } from "../../../utils";
import DescriptionEditor from "../../DescriptionEditor/DescriptionEditor";
import { StageFileUpload } from "../StageFileUpload";

interface ComplaintProps {
  stage: Stage;
  complaint: Complaint | undefined;
  procedureRole: ProcedureRole;
  accountId: number;
  projectId: number;
  activityId: number;
  procedureId: number;
  procedure: Procedure;
}

const Complaints = (props: ComplaintProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const {
    stage,
    complaint,
    procedureRole,
    accountId,
    projectId,
    activityId,
    procedureId,
    procedure,
  } = props;
  const [stageRole, setStageRole] = useState<StageRole>();
  const [updatedDescription, setUpdatedDescription] = useState<string>("");
  const [editMode, setEditMode] = useState<boolean>(false);
  const formStyles = useFormStyles();
  const [uploadedFiles, setUploadedFiles] = useState<ProjectFile[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filteredFiles, setFilteredFiles] = useState<ProjectFile[]>([]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const fetchStageRole = async () => {
    try {
      const data = await getStageRole(projectId, activityId, procedureId, stage.id);
      setStageRole(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancelAction = () => {
    setEditMode(false);
  };

  const handleEditAction = () => {
    setEditMode(true);
  };

  const handleSaveAction = (values: any) => {
    try {
      let newComplaint: ComplaintRequest = ComplaintRequestSchema.parse({
        description: values.field1,
        startDate: values.field2,
        endDate: values.field3,
      });
      saveComplaint(projectId, activityId, procedureId, stage.id, newComplaint)
        .then((response) => {
          setUpdatedDescription(
            response.description ? response.description : ""
          );
          enqueueSnackbar(t("stages.complaints.saved"), {
            variant: "success",
          });
          setEditMode(false);
        })
        .catch((error) => {
          apm.captureError(error);
          enqueueSnackbar(t("stages.complaints.error-saving"), {
            variant: "error",
          });
        });
    } catch (err: any) {
      apm.captureError(err);
      enqueueSnackbar(t("stages.complaints.error-saving"), {
        variant: "error",
      });
    }
  };

  const handleDownload = async (fileId: number, filename: string) => {
    dispatch(setLoading(true));
    try {
      await downloadFile(fileId, filename);
    } catch (error) {
      console.error("Download error:", error);
    }
    dispatch(setLoading(false));
  };

  const handleDeleteExistingFile = async (fileId: number) => {
    try {
      await deleteFile(fileId);
      fetchExistingFiles();
    } catch (error) {
      console.error("Deleting error", error);
    }
  };

  const handleOnFileUpdated = () => {
    fetchExistingFiles();
  };

  const fetchExistingFiles = async () => {
    getStageFiles(stage.id)
      .then((response) => {
        setUploadedFiles(response);
        setFilteredFiles(response);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    setUpdatedDescription(
      complaint?.description ? complaint.description : ""
    );
    fetchExistingFiles();
    fetchStageRole();
  }, []);

  useEffect(() => {
    const lower = search.toLowerCase();
    const filtered = uploadedFiles.filter((file) =>
      file.originalName
        ? file.originalName.toLowerCase().includes(lower)
        : false
    );
    setFilteredFiles(filtered);
    setPage(0);
  }, [search, uploadedFiles]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("generic.stage") + " - " + t(stage.stageTypeLabel)}
      </Typography>

      <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label={t("generic.details")} />
        <Tab label={t("generic.files")} />
      </Tabs>

      {tabIndex === 0 && (
        <Box>
          <Formik
            enableReinitialize
            initialValues={{
              field1: updatedDescription || undefined,
              field2:
                complaint?.startDate ?? null,
              field3:
                complaint?.endDate ?? null,
            }}
            onSubmit={(values) => {
              handleSaveAction(values);
            }}
            validationSchema={Yup.object().shape({
              field1: Yup.string(),
              field2: Yup.date()
                .nullable()
                .transform((value, originalValue) =>
                  originalValue === "" || originalValue === undefined
                    ? null
                    : new Date(originalValue)
                )
                .typeError(t("generic.startDate-valid")),
              field3: Yup.date()
                .nullable()
                .transform((value, originalValue) => {
                  return originalValue === "" || originalValue === undefined
                    ? null
                    : value;
                })
                .typeError(t("generic.endDate-valid"))
                .when("field2", (field2, schema) =>
                  field2
                    ? schema.min(field2, t("generic.endDate-after-startDate"))
                    : schema
                ),
            })}
          >
            {(props) => {
              const {
                values,
                touched,
                errors,
                handleSubmit,
              } = props;
              return (
                <form onSubmit={handleSubmit}>
                  <Grid2 container spacing={3}>
                    <Grid2 size={procedureRole.isManager ? 5 : 6}>
                      {/* Start Date */}
                      <DatePicker
                        label={t("generic.start-date")}
                        timezone="UTC"
                        disabled={!editMode}
                        defaultValue={
                          values.field2 ? moment(values.field2) : null
                        }
                        onChange={(value) =>
                          props.setFieldValue(
                            "field2",
                            value ? value.toDate() : null
                          )
                        }
                        slotProps={{
                          field: {
                            clearable: true,
                          },
                          textField: {
                            variant: editMode ? "outlined" : "standard",
                            helperText:
                              errors.field2 && touched.field2
                                ? errors.field2
                                : "",
                            error:
                              !!(errors.field2 && touched.field2),
                            sx: {...formStyles}
                          },
                        }}
                        format="DD/MM/YYYY"
                        views={["day", "month", "year"]}
                      />
                    </Grid2>
                    <Grid2 size={procedureRole.isManager ? 5 : 6}>
                      {/* End Date */}
                      <DatePicker
                        label={t("generic.end-date")}
                        timezone="UTC"
                        disabled={!editMode}
                        defaultValue={
                          values.field3 ? moment(values.field3) : undefined
                        }
                        onChange={(value) =>
                          props.setFieldValue(
                            "field3",
                            value ? value.toDate() : undefined
                          )
                        }
                        slotProps={{
                          field: {
                            clearable: true,
                          },
                          textField: {
                            variant: editMode ? "outlined" : "standard",
                            helperText:
                              errors.field3 && touched.field3
                                ? errors.field3
                                : "",
                            error:
                              !!(errors.field3 && touched.field3),
                            sx: {...formStyles}
                          },
                        }}
                        format="DD/MM/YYYY"
                        views={["day", "month", "year"]}
                      />
                    </Grid2>
                    {procedureRole.isManager && !procedure.canceled && !procedure.closed && (
                      <Grid2 size={2}>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                        >
                          {!editMode && (
                            <Tooltip title={t("generic.edit")}>
                              <IconButton onClick={handleEditAction}>
                                <EditIcon sx={{ fontSize: 28 }} />
                              </IconButton>
                            </Tooltip>
                          )}

                          {editMode && (
                            <>
                              <Tooltip title={t("generic.cancel")}>
                                <IconButton
                                  onClick={handleCancelAction}
                                  sx={{ color: "red" }}
                                >
                                  <CloseIcon sx={{ fontSize: 28 }} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title={t("generic.save")}>
                                <IconButton
                                  type="submit"
                                  sx={{ color: "green" }}
                                >
                                  <SaveIcon sx={{ fontSize: 28 }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </Grid2>
                    )}
                    <Grid2 size={12}>
                      {/*Description*/}
                      <Box
                        sx={{
                          mb: 2,
                          "& .ProseMirror": {
                            height: "30dvh",
                            overflowY: "auto",
                          },
                        }}
                      >
                        <DescriptionEditor
                          initialText={updatedDescription}
                          isTender={procedureRole.isTender}
                          isEditMode={editMode}
                          onChange={(newValue) => {
                            props.setFieldValue("field1", newValue);
                          }}
                        />
                      </Box>
                    </Grid2>
                  </Grid2>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {tabIndex === 1 && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <TextField
                fullWidth
                label={t("generic.search") ?? "Search"}
                variant="outlined"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mr: 2 }}
              />
              <Tooltip title={t("generic.refresh")}>
                <IconButton onClick={fetchExistingFiles}>
                  <CachedIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Tabela de arquivos */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("generic.file-name") ?? "File name"}
                    </TableCell>
                    <TableCell>{t("generic.created")}</TableCell>
                    <TableCell>{t("generic.account")}</TableCell>
                    <TableCell>{t("generic.size")}</TableCell>
                    <TableCell align="right">
                      {t("generic.actions") ?? "Actions"}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFiles
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((file) => (
                      <TableRow key={file.id}>
                        <TableCell>{file.originalName}</TableCell>
                        <TableCell>
                          {file.createdOn
                            ? formatDate(file.createdOn.toString())
                            : ""}
                        </TableCell>
                        <TableCell>{file.tenderAccountName}</TableCell>
                        <TableCell>{prettyBytes(file.size)}</TableCell>
                        <TableCell align="right">
                          {(stageRole?.isManager ||
                            stageRole?.isTender ||
                            (stageRole?.isOwner && stage.closed)) && (
                            <IconButton
                              onClick={() =>
                                handleDownload(
                                  file.id,
                                  file.originalName ? file.originalName : ""
                                )
                              }
                              color="primary"
                              size="small"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          )}
                          {stageRole!.isTender && !procedure.canceled && !procedure.closed && (
                            <IconButton
                              onClick={() => handleDeleteExistingFile(file.id)}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Paginação */}
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={filteredFiles.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number.parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>

          {stageRole!.isTender === true && !procedure.canceled && !procedure.closed && (
            <StageFileUpload
              accountId={accountId}
              projectId={projectId}
              activityId={activityId}
              procedureId={procedureId}
              stageId={stage.id}
              onFilesUpdated={handleOnFileUpdated}
              procedure={procedure}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default Complaints;
